import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ScanLine,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Camera,
  Keyboard,
  RefreshCw,
  X,
} from "lucide-react";

const REGION_ID = "qr-reader";

export default function UsherScanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [resultOpen, setResultOpen] = useState(false);
  const [scanCount, setScanCount] = useState({ ok: 0, dup: 0, bad: 0 });
  const [manual, setManual] = useState("");
  const [showManual, setShowManual] = useState(false);

  const scannerRef = useRef(null);
  const lastScanRef = useRef({ code: "", at: 0 });

  useEffect(() => {
    api.get("/events").then((r) => {
      setEvents(r.data);
      if (r.data.length === 1) setSelectedEvent(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current.clear().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, []);

  const submitCode = async (code, source = "manual") => {
    const cleaned = (code || "").trim().toUpperCase();
    if (!cleaned) return;

    // De-dupe identical scans within 2 seconds for camera only
    // (the camera fires the decoded callback repeatedly while the QR is in view).
    if (source === "camera") {
      const now = Date.now();
      if (
        lastScanRef.current.code === cleaned &&
        now - lastScanRef.current.at < 2000
      ) {
        return;
      }
      lastScanRef.current = { code: cleaned, at: now };
    }

    try {
      const { data } = await api.post("/scan", { code: cleaned });
      setLastResult(data);
      setResultOpen(true);
      setScanCount((c) => ({
        ok: c.ok + (data.status === "ok" ? 1 : 0),
        dup: c.dup + (data.status === "already_scanned" ? 1 : 0),
        bad: c.bad + (data.status === "invalid" ? 1 : 0),
      }));
      // Beep / vibrate for feedback
      if (navigator.vibrate) {
        navigator.vibrate(data.status === "ok" ? 80 : [60, 50, 60]);
      }
      // Close the camera once we have any result; user resumes via "Scan Next"
      if (scannerRef.current) {
        try {
          await scannerRef.current.stop();
          await scannerRef.current.clear();
        } catch {
          /* ignore */
        }
        scannerRef.current = null;
        setScanning(false);
      }
    } catch (e) {
      setLastResult({ status: "invalid", message: "Network or auth error" });
      setResultOpen(true);
    }
  };

  const startScanning = async () => {
    if (scanning) return;
    setShowManual(false);
    try {
      const html5 = new Html5Qrcode(REGION_ID, { verbose: false });
      scannerRef.current = html5;
      await html5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decoded) => {
          submitCode(decoded, "camera");
        },
        () => {
          /* ignore non-decode frames */
        },
      );
      setScanning(true);
    } catch (e) {
      setLastResult({
        status: "invalid",
        message:
          "Could not start camera. Use manual entry, or check browser camera permissions.",
      });
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch {
        /* ignore */
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    submitCode(manual);
    setManual("");
  };

  const resultBg =
    lastResult?.status === "ok"
      ? "bg-[#00C853] text-white"
      : lastResult?.status === "already_scanned"
        ? "bg-[#FFCC00] text-[#121212]"
        : lastResult
          ? "bg-[#FF3B30] text-white"
          : "bg-white";

  const ResultIcon =
    lastResult?.status === "ok"
      ? CheckCircle2
      : lastResult?.status === "already_scanned"
        ? AlertTriangle
        : XCircle;

  const handleClose = async () => {
    // Stop the camera before navigating away
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch {
        /* ignore */
      }
      scannerRef.current = null;
    }
    setScanning(false);
    setResultOpen(false);
    // Go back to the previous page if there is one, otherwise to the role home.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(user?.role === "admin" ? "/admin" : "/login");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="usher-scanner-page">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <div className="mono-label text-[#666] mb-2">// USHER MODE</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter">
              Scan Tickets
            </h1>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="brut-btn-secondary !py-2 !px-3 inline-flex items-center gap-2 flex-shrink-0"
            data-testid="scanner-close-btn"
            aria-label="Close scanner"
          >
            <X className="w-4 h-4" />
            <span className="hidden sm:inline">Close</span>
          </button>
        </div>

        {/* Event select */}
        <div className="brut-card p-4 mb-5">
          <label htmlFor="ev-select" className="mono-label block mb-2">
            Active Event
          </label>
          <select
            id="ev-select"
            className="brut-input"
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            data-testid="event-select"
          >
            <option value="">— Choose event —</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
              </option>
            ))}
          </select>
        </div>

        {/* Scanner area */}
        <div className="brut-card p-0 overflow-hidden mb-5">
          <div className="bg-[#121212] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-[#FFCC00]" />
              <span className="mono-label">Scanner</span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={
                  "w-2 h-2 rounded-full " +
                  (scanning ? "bg-[#00C853] animate-pulse" : "bg-[#666]")
                }
              />
              <span className="mono-label text-[#A1A1AA]">
                {scanning ? "LIVE" : "OFF"}
              </span>
            </div>
          </div>

          <div className="relative bg-black aspect-square sm:aspect-video flex items-center justify-center">
            <div id={REGION_ID} className="w-full h-full" />
            {!scanning && !showManual && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white p-6">
                <div className="w-16 h-16 border-2 border-[#FFCC00] flex items-center justify-center mb-4">
                  <Camera className="w-8 h-8 text-[#FFCC00]" />
                </div>
                <div className="font-display text-2xl font-black uppercase tracking-tight">
                  Camera Off
                </div>
                <div className="mono-label text-[#A1A1AA] mt-2">
                  Tap start to begin scanning
                </div>
              </div>
            )}
            {scanning && <div className="scanner-laser" />}
          </div>

          <div className="p-4 flex flex-col sm:flex-row gap-3">
            {!scanning ? (
              <button
                onClick={startScanning}
                className="brut-btn-accent flex-1 inline-flex items-center justify-center gap-2"
                data-testid="start-scan-btn"
              >
                <Camera className="w-5 h-5" /> Start Camera
              </button>
            ) : (
              <button
                onClick={stopScanning}
                className="brut-btn flex-1 inline-flex items-center justify-center gap-2"
                data-testid="stop-scan-btn"
              >
                Stop Camera
              </button>
            )}
            <button
              onClick={() => {
                stopScanning();
                setShowManual((s) => !s);
              }}
              className="brut-btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="manual-toggle-btn"
            >
              <Keyboard className="w-5 h-5" /> Manual
            </button>
          </div>

          {showManual && (
            <form
              onSubmit={handleManualSubmit}
              className="border-t-2 border-[#121212] p-4 flex gap-2"
              data-testid="manual-form"
            >
              <input
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                className="brut-input flex-1"
                placeholder="Enter ticket code"
                autoCapitalize="characters"
                autoCorrect="off"
                data-testid="manual-input"
              />
              <button type="submit" className="brut-btn" data-testid="manual-submit">
                Verify
              </button>
            </form>
          )}
        </div>

        {/* Last result modal — opens after every scan, camera is closed */}
        {resultOpen && lastResult && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
            data-testid="scan-result-modal"
            role="dialog"
            aria-modal="true"
          >
            <div
              className={`w-full max-w-md border-2 border-[#121212] shadow-[8px_8px_0px_0px_#121212] ${resultBg}`}
              data-testid={`scan-result-${lastResult.status}`}
            >
              <div className="p-6 sm:p-8 text-center">
                <ResultIcon
                  className="w-20 h-20 mx-auto mb-4"
                  strokeWidth={2.5}
                />
                <div className="mono-label opacity-80 mb-2">
                  {lastResult.status === "ok"
                    ? "// ADMITTED"
                    : lastResult.status === "already_scanned"
                      ? "// DUPLICATE"
                      : "// REJECTED"}
                </div>
                <div className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight leading-tight">
                  {lastResult.message}
                </div>
                {lastResult.ticket && (
                  <div className="mt-4 font-mono text-sm break-all opacity-90">
                    #{lastResult.ticket.serial} · {lastResult.ticket.code}
                  </div>
                )}
                {lastResult.event && (
                  <div className="mt-1 font-mono text-xs opacity-80">
                    {lastResult.event.name}
                  </div>
                )}
              </div>
              <div className="border-t-2 border-[#121212] p-4 flex flex-col sm:flex-row gap-3 bg-white text-[#121212]">
                <button
                  onClick={() => {
                    setResultOpen(false);
                    startScanning();
                  }}
                  className="brut-btn-accent flex-1 inline-flex items-center justify-center gap-2"
                  data-testid="scan-next-btn"
                >
                  <Camera className="w-5 h-5" /> Scan Next
                </button>
                <button
                  onClick={() => setResultOpen(false)}
                  className="brut-btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="scan-result-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Session stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="brut-card p-4 text-center" data-testid="session-ok">
            <div className="mono-label text-[#666]">OK</div>
            <div className="font-display text-3xl font-black text-[#00C853]">
              {scanCount.ok}
            </div>
          </div>
          <div className="brut-card p-4 text-center" data-testid="session-dup">
            <div className="mono-label text-[#666]">DUP</div>
            <div className="font-display text-3xl font-black text-[#FFCC00]">
              {scanCount.dup}
            </div>
          </div>
          <div className="brut-card p-4 text-center" data-testid="session-bad">
            <div className="mono-label text-[#666]">BAD</div>
            <div className="font-display text-3xl font-black text-[#FF3B30]">
              {scanCount.bad}
            </div>
          </div>
        </div>

        <button
          onClick={() => {
            setScanCount({ ok: 0, dup: 0, bad: 0 });
            setLastResult(null);
            setResultOpen(false);
          }}
          className="brut-btn-secondary w-full inline-flex items-center justify-center gap-2"
          data-testid="reset-session-btn"
        >
          <RefreshCw className="w-4 h-4" /> Reset Session
        </button>
      </main>
    </div>
  );
}
